const API_BASE_URL = '/api';

export interface Connection {
  id: string;
  provider: 'EVOLUTION' | 'WAHA' | 'QUEPASA' | 'EVOGO';
  instanceName: string;
  phoneNumber: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ERROR';
  callbackUrl: string;
  webhookSecret: string;
  createdAt: string;
  updatedAt: string;
}

export interface InteractiveCampaign {
  id: string;
  connectionId?: string;
  name: string;
  status: 'DRAFT' | 'SCHEDULED' | 'STARTED' | 'PAUSED' | 'COMPLETED';
  scheduledDate?: string;
  graph: {
    nodes: Record<string, unknown>[];
    edges: Record<string, unknown>[];
    meta?: Record<string, unknown>;
  };
  createdAt: string;
  updatedAt: string;
  connection?: Connection;
}

export interface CreateConnectionDto {
  provider: 'EVOLUTION' | 'WAHA' | 'QUEPASA' | 'EVOGO';
  instanceName: string;
  phoneNumber: string;
}

export interface CreateInteractiveCampaignDto {
  connectionId?: string;
  name: string;
  graph: {
    nodes: Record<string, unknown>[];
    edges: Record<string, unknown>[];
    meta?: Record<string, unknown>;
  };
}

export interface UpdateInteractiveCampaignDto {
  name?: string;
  status?: 'DRAFT' | 'SCHEDULED' | 'STARTED' | 'PAUSED' | 'COMPLETED';
  scheduledDate?: Date;
  graph?: {
    nodes: Record<string, unknown>[];
    edges: Record<string, unknown>[];
    meta?: Record<string, unknown>;
  };
}

export interface ReportData {
  stats: {
    totalSent: number;
    totalDelivered: number;
    totalRead: number;
    totalFailed: number;
    engagementRate: number;
  };
  nodes: Array<{
    id: string;
    type: string;
    label: string;
    stats: {
      reached: number;
      completed: number;
      failed: number;
    };
  }>;
}

export interface SurveyTemplate {
  id: string;
  name: string;
  questions: Array<{
    id: string;
    type: 'text' | 'choice' | 'scale';
    text: string;
    options?: string[];
    required?: boolean;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSurveyTemplateDto {
  name: string;
  questions: Array<{
    type: 'text' | 'choice' | 'scale';
    text: string;
    options?: string[];
    required?: boolean;
  }>;
}

class InteractiveCampaignApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('auth_token');
    const tenantId = localStorage.getItem('selected_tenant_id');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (tenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = tenantId;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  // ========== CONNECTIONS ==========

  async getConnections(): Promise<Connection[]> {
    return this.request<Connection[]>('/connections');
  }

  async getConnection(id: string): Promise<Connection> {
    return this.request<Connection>(`/connections/${id}`);
  }

  async createConnection(data: CreateConnectionDto): Promise<Connection> {
    return this.request<Connection>('/connections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateConnection(
    id: string,
    data: { status?: string; phoneNumber?: string }
  ): Promise<Connection> {
    return this.request<Connection>(`/connections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteConnection(id: string): Promise<void> {
    await this.request<void>(`/connections/${id}`, {
      method: 'DELETE',
    });
  }

  // ========== INTERACTIVE CAMPAIGNS ==========

  async getCampaigns(connectionId?: string): Promise<InteractiveCampaign[]> {
    const queryString = connectionId ? `?connectionId=${connectionId}` : '';
    return this.request<InteractiveCampaign[]>(`/interactive-campaigns${queryString}`);
  }

  async getCampaign(id: string): Promise<InteractiveCampaign> {
    return this.request<InteractiveCampaign>(`/interactive-campaigns/${id}`);
  }

  async createCampaign(data: CreateInteractiveCampaignDto): Promise<InteractiveCampaign> {
    return this.request<InteractiveCampaign>('/interactive-campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampaign(
    id: string,
    data: UpdateInteractiveCampaignDto
  ): Promise<InteractiveCampaign> {
    return this.request<InteractiveCampaign>(`/interactive-campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCampaign(id: string): Promise<void> {
    await this.request<void>(`/interactive-campaigns/${id}`, {
      method: 'DELETE',
    });
  }

  async publishCampaign(id: string, scheduledDate?: Date): Promise<InteractiveCampaign> {
    return this.request<InteractiveCampaign>(`/interactive-campaigns/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify({ scheduledDate }),
    });
  }

  async simulateCampaign(
    id: string,
    data: { from: string; text: string }
  ): Promise<{
    success: boolean;
    executionTrace: Record<string, unknown>[];
    message: string;
  }> {
    return this.request(`/interactive-campaigns/${id}/simulate`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async pauseCampaign(id: string): Promise<InteractiveCampaign> {
    return this.request<InteractiveCampaign>(`/interactive-campaigns/${id}/pause`, {
      method: 'POST',
    });
  }

  async completeCampaign(id: string): Promise<InteractiveCampaign> {
    return this.request<InteractiveCampaign>(`/interactive-campaigns/${id}/complete`, {
      method: 'POST',
    });
  }

  async duplicateCampaign(id: string): Promise<InteractiveCampaign> {
    return this.request<InteractiveCampaign>(`/interactive-campaigns/${id}/duplicate`, {
      method: 'POST',
    });
  }

  async getCampaignReport(id: string): Promise<ReportData> {
    return this.request<ReportData>(`/interactive-campaigns/${id}/report`);
  }

  // ========== SURVEYS ==========

  async getSurveyTemplates(): Promise<SurveyTemplate[]> {
    return this.request<SurveyTemplate[]>('/surveys/templates');
  }

  async createSurveyTemplate(data: CreateSurveyTemplateDto): Promise<SurveyTemplate> {
    return this.request<SurveyTemplate>('/surveys/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const interactiveCampaignApi = new InteractiveCampaignApiService();
